import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { subYears, format } from 'date-fns';
import * as vp from 'vopay';
import useFetchScheduledTransactions from '@/hooks/useApi/useFetchScheduledTransactions';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import { useClientData } from '@/providers/ClientData.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import styles from './ElinxDetails.module.scss';
import { formattedCurrency } from 'vopay';

import { useAlertDialog } from '@/providers/AlertDialog.provider';

const { DetailsCard, Box, SubTitle, Table, Button, TextField, Badge, Icon } = vp;

type TablePropsHandlers = {
  connectionInformation: () => vp.TableProps;
  scheduledInformation: () => vp.TableProps;
  recipientInformation: () => vp.TableProps;
  transactionInformation: () => vp.TableProps;
};

type AccountDetailsProps = {
  eLinx: vp.ELinxHistoryItem;
};

type ConnectionInformation = {
  HoldAmount: string;
  TransactionDate: string;
  LastModified: string;
  TransactionStatus: string;
  FailureReason: string;
  ConvenienceFee: string;
  TransactionType: string;
  Amount: string;
};

type FC = FunctionComponent<AccountDetailsProps>;

const AccountDetails: FC = ({ eLinx }) => {
  const { userSettings } = useUserSettingsProvider();
  const history = useHistory();
  const [scheduledTransactionsResponse, fetchScheduledTransactions] = useFetchScheduledTransactions({});
  const alertDialog = useAlertDialog();
  const [connectionInformationValues, setConnectionInformation] = useState<ConnectionInformation>();
  const [scheduledTransaction, setScheduledTransaction] = useState<vp.ScheduledTransactionsList>();
  const [contactAccountsResponse, fetchClientBankAccounts] = useFetchClientBankAccounts({});
  const { fetchTransactionsHistory } = useFetchTransactionsHistory();

  const {
    response: { Country, LoginUserName, clientAccountsEnabled },
  } = useClientData();

  useEffect(() => {
    if (eLinx.IQ11Token !== '') {
      fetchClientBankAccounts({
        PortalUser: LoginUserName,
        ClientControlled: false,
        Token: eLinx.IQ11Token,
        Timezone: userSettings.timezone,
      });
    }
  }, []);

  const bankAccount = useMemo(() => {
    const found = contactAccountsResponse.BankAccounts[0];

    if (found) {
      return found;
    }

    return null;
  }, [contactAccountsResponse.BankAccounts]);

  useEffect(() => {
    const fetchTransactionData = async () => {
      if (eLinx.ELinxRequestType === 'transaction' && eLinx.TransactionID) {
        try {
          const transaction = await fetchTransactionsHistory({
            StartDateTime: format(subYears(new Date(), 5), 'yyyy-MM-dd'),
            EndDateTime: format(new Date(), 'yyyy-MM-dd'),
            TransactionID: eLinx.TransactionID.toString(),
          });

          // Access the first transaction from the response
          const transactionData = transaction.Transactions?.find(
            (t) => t.TransactionID === String(eLinx.TransactionID),
          );

          if (transactionData) {
            setConnectionInformation({
              TransactionDate: transactionData.TransactionDateTime || '',
              LastModified: transactionData.LastModified || '',
              HoldAmount: transactionData.HoldAmount || '',
              TransactionStatus: transactionData.TransactionStatus || '',
              FailureReason: transactionData.TransactionFailureReason,
              ConvenienceFee: transactionData.ConvenienceFeeAmount || '',
              TransactionType: transactionData.TransactionType || '',
              Amount:
                transactionData.DebitAmount && transactionData.DebitAmount !== '0.00'
                  ? transactionData.DebitAmount
                  : transactionData.CreditAmount,
            });
          }
        } catch (error) {
          alertDialog.error('Error', 'Error fetching transaction history');
          console.error('Error fetching transaction history:', error);
        }
      } else if (eLinx.ELinxRequestType === 'scheduled' && eLinx.ScheduledPaymentsID) {
        fetchScheduledTransactions({
          ScheduledTransactionID: eLinx.ScheduledPaymentsID.toString(),
        });
      }
    };

    fetchTransactionData();
  }, [eLinx]);

  useEffect(() => {
    if (scheduledTransactionsResponse.Success === true) {
      const transactions = Object.values(scheduledTransactionsResponse.ScheduledTransactions).pop();
      setScheduledTransaction(transactions);
    }
  }, [scheduledTransactionsResponse]);

  return (
    <Box flx="col">
      <Box mb="lg">
        <SubTitle
          subTitle={
            eLinx.PaymentType === 'fund'
              ? `A payment of ${eLinx.Currency ?? 'CA'} $${formattedCurrency(
                  connectionInformationValues?.Amount?.toString() || eLinx.Amount?.toString() || '',
                )} has been requested to ${eLinx.ReceiverName}`
              : eLinx.PaymentType === 'withdraw'
                ? `A payment of ${eLinx.Currency ?? 'CA'} $${formattedCurrency(
                    connectionInformationValues?.Amount?.toString() || eLinx.Amount?.toString() || '',
                  )} has been sent to ${eLinx.ReceiverName}`
                : `A payment method connection request has been sent to ${eLinx.ReceiverName}`
          }
        />
      </Box>
      {eLinx.Status === 'pending' && (
        <Box mb="lg" width="100%">
          <DetailsCard
            width="100%"
            content={
              <Box flx="row" gap="lg" width="100%" ai="center">
                <Box ai="center">
                  <SubTitle subTitle="eLinx Embedded Link" variant="small" />
                </Box>
                <Box gap="md" ai="center">
                  <Box maxwidth="700px" pe="sm">
                    <TextField
                      name="Link"
                      variant="lined"
                      height="49px"
                      value={eLinx.Link}
                      onBlur={() => {}}
                      onChange={() => {}}
                      width="700px"
                      readOnly
                    />
                  </Box>

                  <Button
                    id="copy-elinx"
                    label="Copy URL"
                    noShadow
                    onClick={() => {
                      void navigator.clipboard.writeText(eLinx.Link);
                    }}
                  />
                </Box>
              </Box>
            }
          />
        </Box>
      )}
      <Box flx="col">
        <Box flx="col" mb="lg">
          <SubTitle subTitle="Connection Information" variant="small" />
          <Table {...getTableProps('connectionInformation')} />
        </Box>
        {eLinx.ELinxRequestType === 'transaction' && eLinx.Status === 'completed' && (
          <Box flx="col" mb="lg">
            <SubTitle subTitle="Transaction Information" variant="small" />
            <Table {...getTableProps('transactionInformation')} />
          </Box>
        )}
        {eLinx.ELinxRequestType === 'scheduled' && eLinx.Status === 'completed' && (
          <Box flx="col" mb="lg">
            <SubTitle subTitle="Scheduled Transaction Information" variant="small" />
            <Table {...getTableProps('scheduledInformation')} />
          </Box>
        )}
        <Box flx="col" mb="lg">
          <Box gap="ml">
            <SubTitle subTitle="Recipient Information" variant="small" />

            {(eLinx.ContactID || eLinx.ClientAccountID) && (
              <Button
                id="elinx-details"
                label="Details"
                color="secondary"
                textSize={0.8}
                height="28px"
                onClick={() => {
                  if (clientAccountsEnabled) {
                    history.push(`/client-accounts/${eLinx.ContactID}?cardTab=paymentMethods`);
                  } else {
                    history.push(`/contacts/${eLinx.ContactID}?cardTab=paymentMethods`);
                  }
                }}
              />
            )}
          </Box>
          <Table {...getTableProps('recipientInformation')} />
        </Box>
      </Box>
    </Box>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      connectionInformation: () => {
        const ConnectionInfoRows = [
          {
            display: 'eLinx ID',
            value: String(eLinx.ELinxRequestID) || '',
            visible: true,
          },
          {
            display: 'Request Date',
            value: eLinx.RequestDate,
            visible: eLinx.RequestDate?.length >= 1,
          },
          {
            display: 'Status',
            value: (
              <Badge label={capitalizeFirstLetter(eLinx.Status) || ''} color={GetColorStatus(eLinx.Status)} hasIcon />
            ),
            visible: true,
          },
          {
            display: 'Sent To',
            value: eLinx.ReceiverName,
            visible: true,
          },
          {
            display: 'Sender Name',
            value: eLinx.SenderName,
            visible: true,
          },
        ];
        const connectionInformation: vp.TableProps = {
          name: 'connection-information',
          variant: 'pair',
          columns: {
            detailKey: { header: '' },
            detailValue: { header: '' },
          },
          rows: ConnectionInfoRows.filter(({ visible }) => visible).map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return connectionInformation;
      },
      transactionInformation: () => {
        const transactionInfoRows = [
          {
            display: 'Transaction ID',
            value: eLinx.TransactionID ? (
              <Link
                className={styles.link}
                to={`/transactions-history/details/transaction/${eLinx.TransactionID}/${eLinx.Currency}`}
              >
                {eLinx.TransactionID.toString()}
              </Link>
            ) : (
              ''
            ),
            visible: true,
          },

          {
            display: 'Transaction Type',
            value: connectionInformationValues?.TransactionType ?? 'N/A',
            visible: true,
          },
          {
            display: 'Currency',
            value: eLinx.Currency || 'CAD',
            visible: true,
          },
          {
            display: 'Amount',
            value: formattedCurrency(connectionInformationValues?.Amount || ''),
            visible: true,
          },
          {
            display: 'Hold Amount',
            value: formattedCurrency(connectionInformationValues?.HoldAmount || ''),
            visible: true,
          },
          {
            display: 'Convenience Fee',
            value: formattedCurrency(connectionInformationValues?.ConvenienceFee || ''),
            visible: true,
          },
          {
            display: 'Notes',
            value: eLinx.Note ? eLinx.Note : 'N/A',
            visible: true,
          },
        ];
        const transactionInformation: vp.TableProps = {
          name: 'transaction-information',
          variant: 'pair',
          columns: {
            detailKey: { header: '' },
            detailValue: { header: '' },
          },
          rows: transactionInfoRows.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return transactionInformation;
      },
      scheduledInformation: () => {
        const scheduledInfoRows = [
          {
            display: 'Schedule ID',
            value: scheduledTransaction?.ID ? scheduledTransaction?.ID : 'N/A',
            visible: true,
          },
          {
            display: 'Schedule Type',
            value:
              scheduledTransaction?.Frequency === 'recurring'
                ? vp.capitalizeFirstLetterOfWords(scheduledTransaction?.NameOfFrequency)
                : 'Single',
            visible: true,
          },
          {
            display: 'Next Transaction On',
            value:
              scheduledTransaction?.Frequency === 'recurring' && scheduledTransaction?.Status === 'in progress'
                ? getNexTransactionOn(Object.values(scheduledTransaction?.UpcomingTransactions))
                : 'N/A',
            visible: true,
          },
          {
            display: 'End Schedule On',
            value:
              scheduledTransaction?.Frequency === 'recurring'
                ? scheduledTransaction?.EndDate
                  ? scheduledTransaction?.EndDate
                  : `${scheduledTransaction?.EndingAfterPayments || ''} payment(s)`
                : 'N/A',
            visible: true,
          },
          {
            display: 'Schedule Status',
            value: scheduledTransaction?.Status ? (
              <Badge
                label={vp.capitalizeFirstLetterOfWords(scheduledTransaction?.Status || '')}
                color={GetColorStatus(scheduledTransaction?.Status || '')}
                hasIcon
              />
            ) : (
              'N/A'
            ),
            visible: true,
          },
          {
            display: 'Amount',
            value: `${eLinx.Amount || ''}`,
            visible: true,
          },
          {
            display: 'Currency',
            value: eLinx.Currency || 'CAD',
            visible: true,
          },
          {
            display: 'Notes',
            value: eLinx.Note ? eLinx.Note : 'N/A',
            visible: true,
          },
        ];
        const scheduledInformation: vp.TableProps = {
          name: 'scheduled-information',
          variant: 'pair',
          columns: {
            detailKey: { header: '' },
            detailValue: { header: '' },
          },
          rows: scheduledInfoRows.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return scheduledInformation;
      },
      recipientInformation: () => {
        const RecipientInfoRows = [
          {
            display: 'Recipient Name',
            value: eLinx.ReceiverName,
            visible: true,
          },
          {
            display: 'Email Address',
            value: eLinx.ReceiverEmailAddress,
            visible: true,
          },
          {
            display: 'Phone',
            value: eLinx.ReceiverPhoneNumber,
            visible: eLinx.ReceiverPhoneNumber,
          },
          {
            display: 'Address',
            value: `${bankAccount?.Address || ''}, ${bankAccount?.City || ''} ,${
              bankAccount?.['PostalCode/ZipCode'] || ''
            },${Country === 'CA' ? 'Canada' : 'United States '},${bankAccount?.['PostalCode/ZipCode'] || ''}`,
            visible: bankAccount?.Address,
          },
          {
            display: 'Credit Card Number',
            value: (
              <Box flx="row" ai="center" gap="sm">
                {eLinx.CreditCardBrand !== null && (
                  <Icon
                    icon={
                      eLinx.CreditCardBrand === 'mastercard'
                        ? 'cardMaster'
                        : eLinx.CreditCardBrand === 'visa'
                          ? 'cardVisa'
                          : eLinx.CreditCardBrand === 'amex'
                            ? 'cardAE'
                            : eLinx.CreditCardBrand === 'discover'
                              ? 'cardDiscover'
                              : 'creditCard'
                    }
                    color="none"
                    size="xlg"
                    width="40px"
                  />
                )}
                <span>{vp.getMaskedNumber(String(eLinx.CreditCardNumber))}</span>
              </Box>
            ),
            visible: eLinx.CreditCardNumber,
          },
          {
            display: 'Credit Card Expiry',
            value: `${eLinx.CreditCardExpiryMonth || ''} / ${eLinx.CreditCardExpiryYear || ''}`,
            visible: eLinx.CreditCardExpiryMonth || eLinx.CreditCardExpiryYear,
          },
          {
            display: 'Linked Account',
            value: (
              <Box flx="row" ai="center">
                {bankAccount?.BankID !== null && vp.hasBankImage(bankAccount?.BankID || '') && (
                  <img
                    className={styles.logos}
                    src={`https://embed-dev.vopay.com/images/logos-banks/${bankAccount?.BankID || ''}.svg`}
                    alt="bank-logo"
                  />
                )}
                <span>
                  {`${vp.padWithZero(eLinx.FinancialInstitutionNumber?.toString() || '', 3) || ''}-${
                    vp.padWithZero(eLinx.BranchTransitNumber?.toString() || '', 5) || ''
                  }-${vp.getMaskedNumber(eLinx.AccountNumber || '')}`}
                </span>
              </Box>
            ),
            visible: eLinx.FinancialInstitutionNumber || eLinx.BranchTransitNumber || eLinx.AccountNumber,
          },

          {
            display: 'Currency',
            value: eLinx.Currency || 'CAD',
            visible: eLinx.Status === 'completed',
          },
          {
            display: 'Country',
            value: Country === 'CA' ? 'Canada' : 'United States ',
            visible: eLinx.Status === 'completed',
          },
        ];
        const recipientInformation: vp.TableProps = {
          name: 'recipient-information',
          variant: 'pair',
          columns: {
            detailKey: { header: '' },
            detailValue: { header: '' },
          },
          rows: RecipientInfoRows.filter(({ visible }) => visible).map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return recipientInformation;
      },
    };

    return handlers[handler]();
  }
  function GetColorStatus(status: string): 'neutral' | 'positive' | 'negative' {
    if (status === 'pending') {
      return 'neutral';
    }
    if (status === 'completed') {
      return 'positive';
    }
    if (status === 'cancelled') {
      return 'negative';
    }
    if (status === 'declined' || status === 'expired') {
      return 'negative';
    }
    return 'neutral';
  }
  function getNexTransactionOn(upcomings: vp.UpcomingTransactions[]): string {
    if (upcomings.length > 0) {
      const upcoming = upcomings.shift()?.ScheduledDate || 'N/A';
      return upcoming;
    }
    return 'N/A';
  }
  function capitalizeFirstLetter(stringCapitalize: string) {
    return stringCapitalize?.charAt(0).toUpperCase() + stringCapitalize?.slice(1);
  }
};

export default AccountDetails;
